/**
 * Schemas Zod do subsistema de Propostas (PR-M1-01c-i + PR-M1-01c-iii).
 *
 * Evolução (PR-M1-01c-iii.a+.b): proposta agora tem 3 tipos discretos:
 *
 *   - `saas` — só assinatura da plataforma (plano + desconto/custom)
 *   - `consulting` — só consultoria da Karla (parcelas, duração, horas)
 *   - `saas_consulting` — combo: SaaS grátis durante consultoria, depois
 *     vira SaaS pago normal. Ver migration 0046.
 *
 * Discriminated union via Zod: cada `proposal_type` tem schema próprio
 * para os campos obrigatórios. Garante no front que consulting puro
 * não exige plan_key, e consulting/combo exigem campos de parcela.
 *
 * **Input da criação/edição** (`create-proposal` edge function e UPDATE
 * direto em rascunhos) compartilha a estrutura com o banco (migration 0046).
 */
import { z } from 'zod';

// ─── Tipos de proposta ──────────────────────────────────────────────
export const PROPOSAL_TYPES = ['saas', 'consulting', 'saas_consulting'] as const;
export const proposalTypeSchema = z.enum(PROPOSAL_TYPES);
export type ProposalType = z.infer<typeof proposalTypeSchema>;

export const PROPOSAL_TYPE_LABELS: Record<ProposalType, string> = {
  saas: 'Só SaaS',
  consulting: 'Só Consultoria',
  saas_consulting: 'SaaS + Consultoria (combo)',
};

export const PROPOSAL_TYPE_DESCRIPTIONS: Record<ProposalType, string> = {
  saas: 'Assinatura da plataforma. Lead vira cliente SaaS ao assinar.',
  consulting: 'Consultoria da Karla apenas (sem acesso à ferramenta). Parcelas configuráveis.',
  saas_consulting:
    'Pacote combinado. SaaS gratuito enquanto a consultoria estiver vigente; vira SaaS pago normal ao término.',
};

// ─── Status ─────────────────────────────────────────────────────────
export const PROPOSAL_STATUSES = [
  'draft',
  'sent',
  'viewed',
  'in_negotiation',
  'accepted',
  'rejected',
  'expired',
] as const;
export const proposalStatusSchema = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof proposalStatusSchema>;

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Rascunho',
  sent: 'Enviada',
  viewed: 'Visualizada',
  in_negotiation: 'Em negociação',
  accepted: 'Aceita',
  rejected: 'Recusada',
  expired: 'Expirada',
};

// ─── Plano (key dinâmico vindo de public.plans) ─────────────────────
// Antes: enum hardcoded `['free', 'pro', 'enterprise']` + dicionário
// `PLAN_LABELS`. Após PR #159 criar CRUD de planos, qualquer plano novo
// cadastrado em `/superadmin/billing/plans` devia aparecer no dropdown
// de proposta — mas o enum travava em 3 opções. Fix (PR
// fix/proposal-plan-dropdown, 2026-04-24): `plan_key` vira string livre
// validada server-side via lookup em `public.plans`. Display usa
// `plan_name_pt_br` (join vindo do backend) em vez de label hardcoded.
//
// `PLAN_KEYS`/`PLAN_LABELS` continuam exportados como **fallback**
// estático pra casos onde o componente não tem acesso ao catálogo
// (ex: PlatformSettingsPreviewDialog com mock data; IA response schema
// que aceita só os 3 canônicos como hint). Consumers com acesso a
// `LeadProposalSummary` devem usar `plan_name_pt_br` direto.
export const PLAN_KEYS = ['free', 'pro', 'enterprise'] as const;
export const planKeySchema = z.string().trim().min(1).max(64);
export type PlanKey = string;

export const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

// ─── Modo de valor (do SaaS) ────────────────────────────────────────
export const priceModeSchema = z.enum(['discount', 'custom']);
export type PriceMode = z.infer<typeof priceModeSchema>;

// ─── Modalidade e pagamento da consultoria ──────────────────────────
export const CONSULTING_MODALITIES = ['presencial', 'hibrido', 'online'] as const;
export const consultingModalitySchema = z.enum(CONSULTING_MODALITIES);
export type ConsultingModality = z.infer<typeof consultingModalitySchema>;

export const CONSULTING_MODALITY_LABELS: Record<ConsultingModality, string> = {
  presencial: 'Presencial',
  hibrido: 'Híbrido',
  online: 'Online',
};

export const CONSULTING_PAYMENT_METHODS = [
  'pix',
  'asaas_boleto',
  'asaas_credit_card',
  'asaas_pix',
] as const;
export const consultingPaymentMethodSchema = z.enum(CONSULTING_PAYMENT_METHODS);
export type ConsultingPaymentMethod = z.infer<typeof consultingPaymentMethodSchema>;

export const CONSULTING_PAYMENT_METHOD_LABELS: Record<ConsultingPaymentMethod, string> = {
  pix: 'PIX direto',
  asaas_boleto: 'Boleto (Asaas)',
  asaas_credit_card: 'Cartão (Asaas)',
  asaas_pix: 'PIX (Asaas)',
};

// ─── Rascunho devolvido pela IA (sem mudança vs PR anterior) ────────
const aiProposalDraftNormalizedSchema = z
  .object({
    plan_key: planKeySchema,
    price_mode: priceModeSchema,
    discount_percent: z.number().min(0).max(100).nullable().optional(),
    custom_price_brl: z.number().positive().nullable().optional(),
    trial_days: z.number().int().min(0).max(90),
    valid_until_days: z.number().int().min(1).max(90),
    notes_pt_br: z.string().min(50).max(4_000),
  })
  .transform((draft) => ({
    ...draft,
    discount_percent: draft.discount_percent ?? (draft.price_mode === 'discount' ? 0 : null),
    custom_price_brl: draft.custom_price_brl ?? null,
  }));

export const aiProposalDraftSchema = aiProposalDraftNormalizedSchema.refine(
  (v) =>
    (v.price_mode === 'discount' && v.discount_percent !== null && v.custom_price_brl === null) ||
    (v.price_mode === 'custom' && v.custom_price_brl !== null && v.discount_percent === null),
  { message: 'price_mode e valor precisam casar' },
);

export type AiProposalDraft = z.infer<typeof aiProposalDraftSchema>;

export const suggestProposalDraftResponseSchema = z.object({
  mode: z.enum(['live', 'stub']),
  draft: aiProposalDraftSchema,
  model: z.string().optional(),
  provider_key: z.string().optional(),
  tokens_in: z.number().optional(),
  tokens_out: z.number().optional(),
  latency_ms: z.number().optional(),
});

export type SuggestProposalDraftResponse = z.infer<typeof suggestProposalDraftResponseSchema>;

// ─── Campos comuns a todos os tipos de proposta ─────────────────────
const commonProposalFields = {
  lead_id: z.string().uuid(),
  trial_days: z.number().int().min(0).max(90),
  valid_until_days: z.number().int().min(1).max(90),
  notes: z.string().trim().min(10, 'Notas muito curtas').max(4_000),
  // Contexto da IA persistido pra reaproveitamento em re-edições
  // (PR-M1-01c-ii.m, 2026-04-24). Nullable — proposta criada sem IA
  // ou antes desta versão mantém NULL.
  ai_context: z.string().max(4_000).nullable().optional(),
  ai_highlights: z.string().max(2_000).nullable().optional(),
};

// Campos SaaS (aplicam-se a `saas` e `saas_consulting`).
const saasFields = {
  plan_key: planKeySchema,
  price_mode: priceModeSchema,
  discount_percent: z.number().min(0).max(100).nullable(),
  custom_price_brl: z.number().positive().nullable(),
};

// Campos de consultoria (aplicam-se a `consulting` e `saas_consulting`).
// `consulting_payment_method` ficou nullable em 2026-04-24 (PR-M1-01c-ii.d):
// toda cobrança via Asaas `billingType=UNDEFINED` — o lead escolhe PIX/boleto/cartão
// na fatura. Coluna persiste no banco pra back-compat de rascunhos antigos.
const consultingFields = {
  consulting_installments_count: z.number().int().min(1).max(60),
  consulting_installment_brl: z.number().min(0),
  consulting_duration_months: z.number().int().min(1).max(60),
  consulting_weekly_hours: z.number().int().min(0).max(40),
  consulting_extra_hour_brl: z.number().min(0),
  consulting_scope_notes: z.string().trim().max(8_000).nullable(),
  consulting_modality: consultingModalitySchema,
  consulting_payment_method: consultingPaymentMethodSchema.nullable(),
};

// Refinement reutilizável: coerência price_mode ↔ valores.
const saasPriceRefinement = (v: {
  price_mode: PriceMode;
  discount_percent: number | null;
  custom_price_brl: number | null;
}) =>
  (v.price_mode === 'discount' && v.discount_percent !== null && v.custom_price_brl === null) ||
  (v.price_mode === 'custom' && v.custom_price_brl !== null && v.discount_percent === null);

// ─── Input do form (discriminated union por proposal_type) ──────────
//
// Zod 3.x: `discriminatedUnion` valida por campo `proposal_type` e
// garante que cada variante tem seu próprio shape. UI do dialog
// usa `ProposalType` escolhido no select pra renderizar os campos
// certos e submeter a variante correspondente.
// Variantes sem `.refine` — Zod `discriminatedUnion` exige shape direto
// de `z.object` para ler o discriminator. Coerência price_mode fica no
// `.superRefine` da union abaixo.
export const saasProposalInputSchema = z.object({
  proposal_type: z.literal('saas'),
  ...commonProposalFields,
  ...saasFields,
});

export const consultingProposalInputSchema = z.object({
  proposal_type: z.literal('consulting'),
  ...commonProposalFields,
  ...consultingFields,
});

export const saasConsultingProposalInputSchema = z.object({
  proposal_type: z.literal('saas_consulting'),
  ...commonProposalFields,
  ...saasFields,
  ...consultingFields,
  // Flag explícita: no combo, SaaS é gratuito enquanto consultoria
  // vigente. Default true — raramente desligado, mas permite casos
  // especiais (ex: cliente quer SaaS faturado mesmo no combo).
  saas_free_during_consulting: z.boolean().default(true),
});

export const createProposalInputSchema = z
  .discriminatedUnion('proposal_type', [
    saasProposalInputSchema,
    consultingProposalInputSchema,
    saasConsultingProposalInputSchema,
  ])
  .superRefine((v, ctx) => {
    if (v.proposal_type === 'saas' || v.proposal_type === 'saas_consulting') {
      if (!saasPriceRefinement(v)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['price_mode'],
          message: 'Informe desconto% OU preço customizado, conforme o modo escolhido',
        });
      }
    }
  });

export type CreateProposalInput = z.infer<typeof createProposalInputSchema>;

export const createProposalResponseSchema = z.object({
  mode: z.enum(['live', 'stub']),
  proposal_id: z.string(),
  status: z.literal('draft'),
  valid_until: z.string(),
});

export type CreateProposalResponse = z.infer<typeof createProposalResponseSchema>;

// ─── Lista de propostas do lead (consumida pela LeadProposalsSection) ─
//
// Shape retornado pelo SELECT em `public.proposals` filtrado por
// `lead_id`. Inclui o plano associado via embed + todos os campos
// de consultoria/combo pra permitir visualizar e editar o rascunho
// sem nova query (PR-M1-01c-iii.a+.b follow-up).
export const leadProposalSummarySchema = z.object({
  id: z.string().uuid(),
  proposal_type: proposalTypeSchema,
  status: proposalStatusSchema,
  plan_id: z.string().uuid().nullable(),
  plan_key: planKeySchema.nullable(),
  plan_name_pt_br: z.string().nullable(),
  // Campos SaaS
  discount_percent: z.number().nullable(),
  custom_price_brl: z.number().nullable(),
  trial_days: z.number().int().nullable(),
  // Campos de consultoria
  consulting_installments_count: z.number().int().nullable(),
  consulting_installment_brl: z.number().nullable(),
  consulting_duration_months: z.number().int().nullable(),
  consulting_weekly_hours: z.number().int().nullable(),
  consulting_extra_hour_brl: z.number().nullable(),
  consulting_scope_notes: z.string().nullable(),
  consulting_modality: consultingModalitySchema.nullable(),
  consulting_payment_method: consultingPaymentMethodSchema.nullable(),
  // Campos do combo
  saas_free_during_consulting: z.boolean(),
  // Metadata
  valid_until: z.string(),
  notes: z.string().nullable(),
  public_token: z.string().nullable(),
  sent_at: z.string().nullable(),
  viewed_at: z.string().nullable(),
  accepted_at: z.string().nullable(),
  rejected_at: z.string().nullable(),
  questioning_message: z.string().nullable(),
  // Contexto da IA — persistido pra reaproveitar em re-edições (PR-M1-01c-ii.m).
  ai_context: z.string().nullable().default(null),
  ai_highlights: z.string().nullable().default(null),
  // Snapshot pré-negociação (PR-M1-01c-ii.g) — populado quando lead
  // envia dúvida; permite diff highlighting na versão reenviada. Só os
  // campos comerciais (sem IDs ou metadata de sistema).
  pre_negotiation_snapshot: z
    .object({
      discount_percent: z.number().nullable(),
      custom_price_brl: z.number().nullable(),
      trial_days: z.number().int().nullable(),
      consulting_installments_count: z.number().int().nullable(),
      consulting_installment_brl: z.number().nullable(),
      consulting_duration_months: z.number().int().nullable(),
      consulting_weekly_hours: z.number().int().nullable(),
      consulting_extra_hour_brl: z.number().nullable(),
      consulting_modality: consultingModalitySchema.nullable(),
      saas_free_during_consulting: z.boolean().nullable(),
      valid_until: z.string().nullable(),
      notes: z.string().nullable(),
    })
    .nullable()
    .default(null),
  created_at: z.string(),
  updated_at: z.string(),
});

export type PreNegotiationSnapshot = NonNullable<
  z.infer<typeof leadProposalSummarySchema>['pre_negotiation_snapshot']
>;

export type LeadProposalSummary = z.infer<typeof leadProposalSummarySchema>;
